'use client';

import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ObligationResolution } from '@statura/utils';
import { getComplianceObligationsAction } from '@/lib/compliance/obligation-resolution-actions';

/**
 * Resolver-first compliance surfaces (Quality Standards, the Compliance hub)
 * share this hook so they issue one query for the tenant's provider-
 * obligation resolutions and look them up by obligation id. The server
 * action never throws (see getProviderObligationsSafe), so a loader
 * failure surfaces here as an empty map — every decideVisibility call then
 * falls back to legacy visibility, never to a hidden/broken page.
 */
export function useComplianceObligations() {
  // AuthGuard supplies the admitted server profile on first paint, before
  // Zustand is hydrated, and the current verified actor after revalidation.
  const user = useResolvedAuthUser();
  const canRun = Boolean(user?.tenantId && user?.id && user?.role);
  const {
    data: resolutions = [],
    isPending,
    isError,
  } = useQuery<ObligationResolution[]>({
    queryKey: ['compliance-provider-obligations', user?.tenantId, user?.id, user?.role],
    queryFn: () => getComplianceObligationsAction(),
    enabled: canRun,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const resolutionsById = useMemo(
    () => new Map(resolutions.map((resolution) => [resolution.obligationId, resolution])),
    [resolutions],
  );

  return {
    resolutions,
    resolutionsById,
    isPending: canRun && isPending,
    isError,
    isUnavailable: !canRun || isError,
  };
}
